import React, { useCallback, useEffect, useState } from 'react'
import { Plus, ArrowUp, ArrowDown, Archive, ArchiveRestore, Loader2, Check, X, Pencil, GripVertical, CheckCircle2, Search } from 'lucide-react'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge
} from '../ui'
import {
  getDeporteHabilidades,
  createHabilidad,
  updateHabilidad,
  archiveHabilidad,
  restoreHabilidad,
  reorderHabilidades
} from '../../lib/supabase'

const SortableRow = ({
  habilidad,
  idx,
  total,
  isEditing,
  editingNombre,
  onChangeEditingNombre,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onMoveUp,
  onMoveDown,
  onArchivar,
  onRestaurar,
  showArchived,
  sortDisabled = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: habilidad.id, disabled: isEditing || sortDisabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : (habilidad.activo ? 1 : 0.6),
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 rounded-md border px-3 py-1.5 transition-colors ${
        isDragging
          ? 'border-blue-300 bg-blue-50 shadow-md'
          : 'border-border hover:bg-muted/50'
      } ${!habilidad.activo ? 'bg-muted/30' : ''}`}
    >
      {/* Drag handle */}
      {habilidad.activo && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground/50 hover:text-muted-foreground disabled:opacity-30"
          aria-label="Arrastrar para reordenar"
          disabled={isEditing}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      {/* Botones de mover como fallback de accesibilidad (solo activas) */}
      {habilidad.activo && (
        <div className="flex flex-col">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => onMoveUp(idx)}
            disabled={idx === 0 || isEditing}
            aria-label="Subir"
          >
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => onMoveDown(idx)}
            disabled={idx === total - 1 || isEditing}
            aria-label="Bajar"
          >
            <ArrowDown className="h-3 w-3" />
          </Button>
        </div>
      )}

      {!habilidad.activo && (
        <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
          Archivada
        </Badge>
      )}

      {isEditing ? (
        <>
          <Input
            autoFocus
            value={editingNombre}
            onChange={(e) => onChangeEditingNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveEdit()
              if (e.key === 'Escape') onCancelEdit()
            }}
            className="flex-1 h-8"
          />
          <Button size="icon" variant="ghost" onClick={onSaveEdit} aria-label="Guardar">
            <Check className="h-4 w-4 text-green-600" />
          </Button>
          <Button size="icon" variant="ghost" onClick={onCancelEdit} aria-label="Cancelar">
            <X className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => habilidad.activo && onStartEdit(habilidad)}
            disabled={!habilidad.activo}
            className="flex-1 text-left text-sm py-1 disabled:cursor-default"
          >
            {habilidad.nombre}
          </button>
          {habilidad.activo && (
            <Button
              size="icon"
              variant="ghost"
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onStartEdit(habilidad)}
              aria-label="Editar"
            >
              <Pencil className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
          {habilidad.activo ? (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onArchivar(habilidad)}
              aria-label="Archivar"
            >
              <Archive className="h-4 w-4 text-muted-foreground" />
            </Button>
          ) : showArchived ? (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onRestaurar(habilidad)}
              aria-label="Restaurar"
            >
              <ArchiveRestore className="h-4 w-4 text-blue-600" />
            </Button>
          ) : null}
        </>
      )}
    </div>
  )
}

const HabilidadesManager = ({ deporte, orgId, deporteLabel, confirm, alert: alertDialog }) => {
  const [habilidades, setHabilidades] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [mensajeExito, setMensajeExito] = useState(null)
  const [nuevoNombre, setNuevoNombre] = useState('')
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editingNombre, setEditingNombre] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [search, setSearch] = useState('')

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const loadHabilidades = useCallback(async () => {
    if (!deporte) return
    setLoading(true)
    setError(null)
    const { data, error: err } = await getDeporteHabilidades(deporte, { incluirInactivas: showArchived })
    if (err) {
      setError('No pudimos cargar las habilidades')
    } else {
      setHabilidades(data || [])
    }
    setLoading(false)
  }, [deporte, showArchived])

  useEffect(() => {
    loadHabilidades()
  }, [loadHabilidades])

  // Auto-clear del mensaje de exito
  useEffect(() => {
    if (!mensajeExito) return
    const t = setTimeout(() => setMensajeExito(null), 2500)
    return () => clearTimeout(t)
  }, [mensajeExito])

  const activas = habilidades.filter((h) => h.activo)
  const archivadas = habilidades.filter((h) => !h.activo)

  const handleCrear = async () => {
    const nombre = nuevoNombre.trim()
    if (nombre.length < 2) {
      setError('El nombre debe tener al menos 2 caracteres')
      return
    }
    setCreating(true)
    setError(null)
    const { error: err } = await createHabilidad({
      deporte,
      nombre,
      org_id: orgId
    })
    setCreating(false)
    if (err) {
      setError(err.message || 'No pudimos crear la habilidad')
      return
    }
    setNuevoNombre('')
    setMensajeExito(`Habilidad "${nombre}" agregada`)
    await loadHabilidades()
  }

  const startEdit = (habilidad) => {
    setEditingId(habilidad.id)
    setEditingNombre(habilidad.nombre)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingNombre('')
  }

  const saveEdit = async () => {
    const nombre = editingNombre.trim()
    if (nombre.length < 2) {
      setError('El nombre debe tener al menos 2 caracteres')
      return
    }
    const { error: err } = await updateHabilidad(editingId, { nombre })
    if (err) {
      setError(err.message || 'No pudimos actualizar')
      return
    }
    setMensajeExito('Cambios guardados')
    cancelEdit()
    await loadHabilidades()
  }

  const handleArchivar = async (habilidad) => {
    const ok = await confirm(
      'Archivar habilidad',
      `¿Archivar "${habilidad.nombre}"? Los programas existentes que la usen la conservaran, pero no aparecera como opcion para nuevos programas.`,
      { destructive: true, confirmText: 'Archivar' }
    )
    if (!ok) return

    const { error: err } = await archiveHabilidad(habilidad.id)
    if (err) {
      await alertDialog('Error', err.message || 'No pudimos archivar')
      return
    }
    setMensajeExito(`"${habilidad.nombre}" archivada`)
    await loadHabilidades()
  }

  const handleRestaurar = async (habilidad) => {
    const { error: err } = await restoreHabilidad(habilidad.id)
    if (err) {
      await alertDialog('Error', err.message || 'No pudimos restaurar')
      return
    }
    setMensajeExito(`"${habilidad.nombre}" restaurada`)
    await loadHabilidades()
  }

  const moveUp = async (idx) => {
    if (idx === 0) return
    const nuevoOrden = [...activas]
    ;[nuevoOrden[idx - 1], nuevoOrden[idx]] = [nuevoOrden[idx], nuevoOrden[idx - 1]]
    // Update local state preservando archivadas
    setHabilidades([...nuevoOrden, ...archivadas])
    await reorderHabilidades(nuevoOrden.map((h) => h.id))
  }

  const moveDown = async (idx) => {
    if (idx === activas.length - 1) return
    const nuevoOrden = [...activas]
    ;[nuevoOrden[idx + 1], nuevoOrden[idx]] = [nuevoOrden[idx], nuevoOrden[idx + 1]]
    setHabilidades([...nuevoOrden, ...archivadas])
    await reorderHabilidades(nuevoOrden.map((h) => h.id))
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIdx = activas.findIndex((h) => h.id === active.id)
    const newIdx = activas.findIndex((h) => h.id === over.id)
    if (oldIdx < 0 || newIdx < 0) return

    const nuevoOrden = arrayMove(activas, oldIdx, newIdx)
    setHabilidades([...nuevoOrden, ...archivadas])
    await reorderHabilidades(nuevoOrden.map((h) => h.id))
  }

  // Filtrar y reordenar por arrastre no se llevan: con un filtro activo el usuario
  // moveria items contra posiciones que no ve. Por eso el drag se desactiva al buscar.
  const term = search.trim().toLowerCase()
  const matchSearch = (h) => !term || (h.nombre || '').toLowerCase().includes(term)
  const activasFiltradas = activas.filter(matchSearch)
  const archivadasFiltradas = archivadas.filter(matchSearch)
  const visibles = showArchived
    ? [...activasFiltradas, ...archivadasFiltradas]
    : activasFiltradas

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center justify-between gap-2">
          <span>Habilidades de {deporteLabel || deporte}</span>
          <div className="flex items-center gap-2">
            {!loading && activas.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground">
                {activas.length} {activas.length === 1 ? 'habilidad' : 'habilidades'}
              </span>
            )}
            {archivadas.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => setShowArchived((s) => !s)}
              >
                {showArchived
                  ? `Ocultar archivadas (${archivadas.length})`
                  : `Mostrar archivadas (${archivadas.length})`}
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {mensajeExito && (
          <Alert variant="success">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>{mensajeExito}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label htmlFor={`nueva-habilidad-${deporte}`}>Nueva habilidad</Label>
            <Input
              id={`nueva-habilidad-${deporte}`}
              value={nuevoNombre}
              onChange={(e) => setNuevoNombre(e.target.value)}
              placeholder="Ej: Ofensiva, Velocidad, Reaccion..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCrear()
              }}
            />
          </div>
          <Button onClick={handleCrear} disabled={creating || !nuevoNombre.trim()}>
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Plus className="h-4 w-4 mr-1" />
                Agregar
              </>
            )}
          </Button>
        </div>

        {/* La busqueda aparece solo cuando la lista es larga: con 4 habilidades estorba */}
        {!loading && activas.length > 6 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar habilidad..."
              className="pl-10"
            />
          </div>
        )}

        {term && (
          <p className="text-xs text-muted-foreground">
            Mostrando {visibles.length} de {activas.length}. Quita la búsqueda para reordenar arrastrando.
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Cargando...
          </div>
        ) : visibles.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            {term
              ? `No hay habilidades que coincidan con "${search.trim()}".`
              : 'Aun no hay habilidades. Agrega la primera arriba.'}
          </div>
        ) : (
          <div className="space-y-1">
            {/* Habilidades activas — sortable */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={activasFiltradas.map((h) => h.id)}
                strategy={verticalListSortingStrategy}
              >
                {activasFiltradas.map((habilidad) => (
                  <SortableRow
                    key={habilidad.id}
                    habilidad={habilidad}
                    idx={activas.findIndex((h) => h.id === habilidad.id)}
                    total={activas.length}
                    isEditing={editingId === habilidad.id}
                    editingNombre={editingNombre}
                    onChangeEditingNombre={setEditingNombre}
                    onStartEdit={startEdit}
                    onCancelEdit={cancelEdit}
                    onSaveEdit={saveEdit}
                    onMoveUp={moveUp}
                    onMoveDown={moveDown}
                    onArchivar={handleArchivar}
                    onRestaurar={handleRestaurar}
                    showArchived={showArchived}
                    sortDisabled={Boolean(term)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {/* Habilidades archivadas — solo si showArchived */}
            {showArchived && archivadasFiltradas.length > 0 && (
              <div className="pt-2 mt-2 border-t border-dashed border-border">
                <p className="text-xs text-muted-foreground mb-1">Archivadas</p>
                {archivadasFiltradas.map((habilidad) => (
                  <SortableRow
                    key={habilidad.id}
                    habilidad={habilidad}
                    idx={-1}
                    total={0}
                    isEditing={false}
                    editingNombre=""
                    onChangeEditingNombre={() => {}}
                    onStartEdit={() => {}}
                    onCancelEdit={() => {}}
                    onSaveEdit={() => {}}
                    onMoveUp={() => {}}
                    onMoveDown={() => {}}
                    onArchivar={() => {}}
                    onRestaurar={handleRestaurar}
                    showArchived={showArchived}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default HabilidadesManager
